import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import cn from "../../utils/cn"

// Colour follows the match source, never its position, so a source keeps its
// colour when another one drops to zero. The two lookups that resolve a
// physician share the blue family; Not Found is the neutral "no match" grey.
const MATCH_SOURCES = [
  { key: "SPECIALTY_DB_LOOKUP", label: "Enrichment DB", color: "#1d4ed8" },
  { key: "NPPES_LOOKUP", label: "NPPES API", color: "#60a5fa" },
  { key: "NAME_VARIANT_LOOKUP", label: "Name variant", color: "#f59e0b" },
  { key: "NOT_FOUND", label: "Not found", color: "#9ca3af" },
]

const PERSONALIZED_COLOR = "#1d4ed8"
const GENERIC_COLOR = "#f59e0b"

const formatPercent = (value, total) => {
  if (!total || !value) return "0%"
  const share = (value / total) * 100
  return share < 0.1 ? "<0.1%" : `${share.toFixed(1)}%`
}

const Panel = ({ title, meta, children }) => (
  <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      <span className="text-xs text-gray-500">{meta}</span>
    </div>
    {children}
  </div>
)

/*
  One bar split into segments that together make the panel's whole. Zero
  segments are dropped so they don't leave an empty gap.
*/
const StackedBar = ({ segments, total }) => (
  <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-gray-100">
    {total > 0 &&
      segments
        .filter((segment) => segment.value > 0)
        .map((segment) => (
          <div
            key={segment.key}
            className="h-full"
            style={{
              width: `${(segment.value / total) * 100}%`,
              backgroundColor: segment.color,
            }}
            title={`${segment.label}: ${segment.value.toLocaleString()} (${formatPercent(
              segment.value,
              total
            )})`}
          />
        ))}
  </div>
)

const LegendRow = ({ marker, label, hint, value, total, valueClassName }) => (
  <div className="flex items-center gap-3 py-2.5" title={hint}>
    <span className="flex w-3 shrink-0 justify-center">{marker}</span>
    <span
      className={cn(
        "flex-1 text-sm",
        value ? "text-gray-700" : "text-gray-400"
      )}
    >
      {label}
    </span>
    <span
      className={cn(
        "w-16 text-right text-sm font-semibold tabular-nums",
        valueClassName || "text-gray-900"
      )}
    >
      {(value ?? 0).toLocaleString()}
    </span>
    <span className="w-14 text-right text-sm tabular-nums text-gray-500">
      {formatPercent(value, total)}
    </span>
  </div>
)

const Swatch = ({ color }) => (
  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
)

/*
  What happened to the period's QR codes. Personalized + generic make up the
  attached total (the bar); failed sits outside it and is shown against all QR
  codes, since a failed QR code was never attached.
*/
export const QrDeliveryPanel = ({ total, attached, generic, failed }) => {
  const personalized = Math.max((attached || 0) - (generic || 0), 0)

  return (
    <Panel
      meta={`${(attached || 0).toLocaleString()} attached`}
      title="QR delivery breakdown"
    >
      <StackedBar
        segments={[
          {
            key: "personalized",
            label: "Personalized video",
            value: personalized,
            color: PERSONALIZED_COLOR,
          },
          {
            key: "generic",
            label: "Generic video fallback",
            value: generic || 0,
            color: GENERIC_COLOR,
          },
        ]}
        total={attached}
      />

      <div className="divide-y divide-gray-100">
        <LegendRow
          hint="Attached QR codes whose referring physician was matched, so the report got a personalized video"
          label="Personalized video"
          marker={<Swatch color={PERSONALIZED_COLOR} />}
          total={attached}
          value={personalized}
        />
        <LegendRow
          hint="Attached QR codes whose referring physician was not found, so the report got a generic video"
          label="Generic video fallback"
          marker={<Swatch color={GENERIC_COLOR} />}
          total={attached}
          value={generic}
        />
        <LegendRow
          hint="Distinct QR codes (accession numbers) with at least one failed matching record in this period"
          label="Failed to attach"
          marker={
            <FontAwesomeIcon
              className="h-3 w-3 text-red-600"
              icon={faCircleExclamation}
            />
          }
          total={total}
          value={failed}
          valueClassName={failed ? "text-red-600" : undefined}
        />
      </div>
    </Panel>
  )
}

/*
  How the period's referring doctors were matched. Counts are distinct doctors,
  so the sources add up to the unique total and the bar reads as one whole.
*/
export const DoctorMatchPanel = ({ uniqueDoctors, categories }) => {
  const rows = MATCH_SOURCES.map((source) => {
    const category = (categories || []).find((c) => c.key === source.key)
    return {
      ...source,
      value: category?.doctorCount || 0,
      qrCodes: category?.distinctCount || 0,
    }
  })
  const matched = rows
    .filter((row) => row.key !== "NOT_FOUND")
    .reduce((sum, row) => sum + row.value, 0)

  return (
    <Panel
      meta={`${(uniqueDoctors || 0).toLocaleString()} unique · ${matched.toLocaleString()} matched`}
      title="Referring doctors — match source"
    >
      <StackedBar segments={rows} total={uniqueDoctors} />

      <div className="divide-y divide-gray-100">
        {rows.map((row) => (
          <LegendRow
            key={row.key}
            hint={`Distinct doctors with match source ${row.key} (${row.qrCodes} QR codes)`}
            label={row.label}
            marker={<Swatch color={row.color} />}
            total={uniqueDoctors}
            value={row.value}
          />
        ))}
      </div>
    </Panel>
  )
}
